/**
 * Focus Engine — every design has exactly one primary focal point.
 *
 * The single most common amateur mistake is three things shouting at once.
 * This engine decides WHAT wins — the hero image, the headline, or the
 * offer — from the audience's reading habit and the mood's energy, and
 * demotes everything else to supporting roles. The invariant (exactly one
 * primary) is enforced structurally: `primary` is a single value, and the
 * chosen candidate is removed from `supporting`.
 */

export const FOCAL_CANDIDATES = ['hero', 'primaryHeading', 'offer'];

/**
 * Decide the focal point.
 * Returns { primary, supporting: [..], deemphasized: [..], rationale }.
 */
export const decideFocus = ({ audience, mood, rng }) => {
  let primary;
  let rationale;

  if (audience.readingHabit === 'image-first' && audience.heroImageImportance === 'high') {
    primary = 'hero';
    rationale = `${audience.name} audiences buy with their eyes — the hero image carries the design.`;
  } else if (mood.energy >= 0.75) {
    primary = 'offer';
    rationale = 'High-energy brief — the offer is the loudest thing on the page.';
  } else {
    primary = 'primaryHeading';
    rationale = 'Message-led brief — the headline does the heavy lifting.';
  }

  // Occasionally let a strong secondary candidate take over (keeps batches
  // from becoming formulaic) — but the invariant never bends: one primary.
  if (rng() < 0.15) {
    const alternates = FOCAL_CANDIDATES.filter((c) => c !== primary);
    primary = alternates[Math.floor(rng() * alternates.length)];
    rationale += ' (director’s override: intentional focal variation)';
  }

  const supporting = FOCAL_CANDIDATES.filter((c) => c !== primary);
  return {
    primary,
    supporting,
    deemphasized: ['secondaryHeading', 'body', 'footer'],
    rationale,
  };
};
